<template>
  <div>
    <div class="sm:flex sm:justify-between">
      <label class="w-full block text-gray-700 flex-1">
        Message
        <span class="text-xs text-gray-400">({{ messageSegments.encodingName }})</span>
      </label>
      <div class="flex-1 flex justify-start sm:justify-end">
        <p class="w-40 sm:text-right sm:flex-1 text-xs mr-2">
                    <span class="text-right text-xs mr-1">
                        {{ hasVariables ? 'Estimated' : '' }} Segments: {{ messageSegments.segmentsCount }} |
                    </span>
          Characters: {{ hasVariables ? '-' : characterCount }}
        </p>
        <tooltip position="left">
          <h3 class="font-bold text-lg mb-2">What's a segment?</h3>
          <p class="mb-2 font-normal">SMS segments are the blocks of characters (letters, numbers, spaces, punctuation, etc) that an individual SMS message is broken into.</p>
          <p class="mb-2 font-normal">Segments are usually 160 characters for the GSM-7 format and 70 characters for the UCS-2 format. GSM-7 is the standard format, but UCS-2 is almost always used if your message contains emojis.</p>
          <p class="font-normal underline">Messages over the character limit will be sent in multiple segments. <span>Each segment you send counts as one credit.</span> These rules are set by wireless carriers, not TextSpot.</p>
        </tooltip>
      </div>
    </div>
    <div class="editor-container rounded">
            <textarea
              class="editor-textarea w-full p-2 border-0 rounded-t"
              v-model="fields.message"
              ref="messageInput"
              rows="5"
            ></textarea>
      <div class="editor-toolbar flex p-2 bg-gray-100 relative">
        <button
          class="text-right text-xs sm:text-sm cursor-pointer px-4 py-2 mr-2 bg-gray-100 hover:bg-gray-300 rounded-full upload-image"
          @click.prevent="toggleImageUpload"
          :disabled="uploadImageFormVisible"
        >
          <font-awesome-icon :icon="['far', 'image']" /> Add Image
        </button>
        <CreateLinkButton
          @input="handleVariableInput($event)"
          :messageHasLink="hasLinks"
          class="ml-2"
        />
        <CustomFieldSelector
          @select="handleVariableInput($event.tag)"
        />
        <UseTemplate @use-template="insertTemplateContent" />
      </div>
    </div>
    <MessageFormImageDrop
      v-if="uploadImageFormVisible"
      v-model="fields.media"
      @error="handleMediaError"
      @preview="data => $emit('image-preview', data)"
    />
  </div>
</template>

<script>
import CreateLinkButton from "../CreateLinkButton.vue";
import UseTemplate from "../UseTemplate.vue";
import { SegmentedMessage } from 'sms-segments-calculator';
import CustomFieldSelector from "../CustomFieldSelector.vue";
import MessageFormImageDrop from "./MessageFormImageDrop.vue";

export default {
  components: {CreateLinkButton,UseTemplate, CustomFieldSelector, MessageFormImageDrop},
  props: ['modelValue', 'error'],
  emits: ['update:modelValue', 'image-preview'],
  data: function () {
    return {
      uploadImageFormVisible: false,
      segment_lengths: {
        sms_ucs: 70,
        sms_gsm: 160,
        mms_ucs: 1000,
        mms_gsm: 1600
      },
      sms_segment_credit: 1,
      mms_segment_credit: 3,
    }
  },
  mounted() {
    if (this.value.media) {
      this.uploadImageFormVisible = true;
    }
  },
  computed: {
    characterCount() {
      return this.fields.message.length;
    },
    messageSegments() {
      return new SegmentedMessage(this.fields.message);
    },
    hasVariables() {
      const pattern = /\{[^}]*\}/;
      return pattern.test(this.fields.message);
    },
    fields() {
      return this.value
    },
    hasLinks() {
      const linkPattern = /(?<!\w)(?:(https?:\/\/)?(?:www\.)?([a-zA-Z0-9-]+\.)+[a-z]{2,})(?=\s|[^\w]|\/|:|$)/g;
      const shortenedPattern = /\{\s?link url="((?:https?:\/\/)?(?:[^\s\/"\\]+\.[^\s\/"\\]+)+[^\s"\\]*)"(?:\s+track)?\s?}/g;

      const shortenedPatternMatch = [...this.fields.message.matchAll(new RegExp(shortenedPattern))];
      const linkPatternMatch = [...this.fields.message.matchAll(new RegExp(linkPattern))]

      if(shortenedPatternMatch.length) return shortenedPatternMatch.length !== linkPatternMatch.length;

      return  linkPatternMatch.length ? !!linkPatternMatch.length : false;
    },
    value() {
        return this.modelValue;
    }
   },
  methods: {
     insertTemplateContent(template) {
      this.fields.message = '';
      this.fields.media = null;
      const textarea = this.$refs.messageInput;
      const messageStart = this.fields.message.slice(0, textarea.selectionStart);
      const messageEnd = this.fields.message.slice(textarea.selectionEnd);
      const newCursorPos = textarea.selectionStart + template.unparsed_message.length;
      this.fields.message = messageStart + template.unparsed_message + messageEnd;
       if (template.media) {
        this.$emit('image-preview', template.media);
        this.fields.media = template.media;
        this.uploadImageFormVisible = true;
    } else {
      this.fields.media = null;
      this.uploadImageFormVisible = false;
      this.$emit('image-preview', null);
    }
      this.$nextTick(() => {
        textarea.focus();
        textarea.selectionStart = newCursorPos;
        textarea.selectionEnd = newCursorPos;
      });
    },
    toggleImageUpload() {
      this.uploadImageFormVisible = !this.uploadImageFormVisible;
    },
    handleVariableInput(value) {
      const textarea = this.$refs.messageInput;
      const messageStart = this.fields.message.slice(0, textarea.selectionStart);
      const messageEnd = this.fields.message.slice(textarea.selectionEnd);
      const newCursorPos = textarea.selectionStart + value.length;
      this.fields.message = messageStart + value + messageEnd;
      this.$nextTick(() => {
        textarea.focus();
        textarea.selectionStart = newCursorPos;
        textarea.selectionEnd = newCursorPos;
      });
    },
    handleMediaError(error) {
      console.log(error);
    },
  },
  watch: {
    fields: {
      handler(newFields) {
        this.$emit('update:modelValue', newFields);
      },
      deep: true
    },
    value: {
      handler() {
        if (this.value && this.value.media) {
          this.uploadImageFormVisible = true;
        }
      },
      deep:true,
      immediate: true,
    }
  }
}
</script>

<style scoped>

.upload-image:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.editor-container {
  padding: 0px;
  border: 1px solid #ccc;
  width: 100%;
}

.editor-textarea:focus {
  border-color: transparent;
  outline: none;
}

.editor-container:focus-within {
  border-color: blue;
}

.editor-textarea {
  border-bottom: 1px solid #eee;
}
</style>
